import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    VStack,
    Box,
    Badge,
    HStack,
    useToast,
    Spinner,
    Flex,
    Alert,
    AlertIcon,
    AlertDescription,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    Icon,
    IconButton,
    ModalCloseButton,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
} from '@chakra-ui/react';
import { useState, useEffect, useMemo, useRef } from 'react';
import { TbPencil, TbInfoCircle, TbChevronDown } from 'react-icons/tb';
import { AxiosError } from 'axios';
import { formatPrice } from '../functions';
import { usePlaceOrder } from '../hooks/usePlaceOrder';
import { useGetOrderConfirmation } from '../hooks/useGetOrderConfirmation';
import { useUpdateOrderDetails } from '../hooks/useUpdateOrderDetails';
import PartBayButton from '../components/PartBayButton';
import DatePicker from '../components/DatePicker';

interface PlaceOrderConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
}

type OrderType = 'asap' | 'tomorrow' | 'twoDays' | 'custom';

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
    asap: 'ASAP',
    tomorrow: 'Tomorrow',
    twoDays: 'In Two Days',
    custom: 'Custom Date',
};

const startOfTomorrow = (): Date => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    return d;
};

const startOfTwoDays = (): Date => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    d.setHours(8, 0, 0, 0);
    return d;
};

const formatDateShort = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const formatScheduledDateLong = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const PlaceOrderConfirmationModal = ({
    isOpen,
    onClose,
    orderId,
}: PlaceOrderConfirmationModalProps) => {
    const placeOrder = usePlaceOrder();
    const { data, isLoading } = useGetOrderConfirmation(orderId, isOpen);
    const updateOrderDetailsMutation = useUpdateOrderDetails();
    const toast = useToast();

    const [isEditingRoNumber, setIsEditingRoNumber] = useState(false);
    const [editedRoNumber, setEditedRoNumber] = useState('');
    const initializedRoForOpen = useRef(false);
    const [orderType, setOrderType] = useState<OrderType>('asap');
    const [customDate, setCustomDate] = useState<Date>(startOfTomorrow());
    const [isOeConfirmOpen, setIsOeConfirmOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            initializedRoForOpen.current = false;
            setIsEditingRoNumber(false);
            return;
        }
        setOrderType('asap');
        setCustomDate(startOfTomorrow());
        setIsOeConfirmOpen(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !data) return;
        if (!initializedRoForOpen.current) {
            initializedRoForOpen.current = true;
            setEditedRoNumber(data.roNumber);
            setIsEditingRoNumber(data.roNumber.length === 0);
            return;
        }
        if (!isEditingRoNumber) {
            setEditedRoNumber(data.roNumber);
        }
    }, [isOpen, data, isEditingRoNumber]);

    const hasRoNumber = (data?.roNumber?.length ?? 0) > 0 || editedRoNumber.trim().length > 0;
    const hasInvalidMessage = !!data?.invalidMessage;
    const cannotOrderTag = data?.quotesOnly ? 'quote only' : data?.potentialTotal ? 'potential total' : null;
    const isQuoteWorkflow = !!data?.quotesOnly || !!data?.potentialTotal;

    const scheduledDate = useMemo<Date | null>(() => {
        switch (orderType) {
            case 'asap': return null;
            case 'tomorrow': return startOfTomorrow();
            case 'twoDays': return startOfTwoDays();
            case 'custom': {
                const d = new Date(customDate);
                d.setHours(8, 0, 0, 0);
                return d;
            }
        }
    }, [orderType, customDate]);

    const orderTypeDisplay = useMemo(() => {
        const base = ORDER_TYPE_LABELS[orderType];
        if (orderType === 'asap') return base;
        if (scheduledDate) return `${base} — ${formatDateShort(scheduledDate)}`;
        return base;
    }, [orderType, scheduledDate]);

    const handleRoNumberSave = () => {
        if (data && editedRoNumber.trim() !== data.roNumber) {
            updateOrderDetailsMutation.mutate({
                orderId,
                roNumber: editedRoNumber.trim()
            });
        }
        setIsEditingRoNumber(false);
    };

    const handleConfirmPlaceOrder = () => {
        if (isQuoteWorkflow || data?.invalidMessage) return;
        if (data?.oeCertificationPopup) {
            setIsOeConfirmOpen(true);
            return;
        }
        void doPlaceOrder();
    };

    const doPlaceOrder = async () => {
        if (isQuoteWorkflow || data?.invalidMessage) return;
        try {
            await placeOrder.mutateAsync({
                orderId,
                scheduledFor: scheduledDate ? scheduledDate.toISOString() : null,
            });
            setIsOeConfirmOpen(false);
            onClose();
            toast({
                title: "Order queued successfully",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 409) {
                toast({
                    title: "Order queued, please check back shortly",
                    status: "info",
                    duration: 5000,
                    isClosable: true,
                });
            }
            if (error instanceof AxiosError && error.response?.status === 422) {
                toast({
                    title: error.response.data?.message ?? "Error placing order, pleae verify selections",
                    status: "error",
                    duration: 10000,
                    isClosable: true,
                });
            }
            setIsOeConfirmOpen(false);
            onClose();
        }
    };

    return (
        <>
        <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="outside" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl" minH="60vh">
                <ModalHeader color="gray.800" fontSize="lg" fontWeight="semibold">
                    Confirm Order Placement
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody>
                    {isLoading || !data ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="lg" />
                        </Flex>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {hasInvalidMessage ? (
                                <Alert status="error" borderRadius="md" border="1px solid" borderColor="red.300" bg="red.50">
                                    <AlertIcon color="red.500" />
                                    <AlertDescription fontSize="sm" color="red.800">
                                        <strong>Cannot place order:</strong> {data.invalidMessage}
                                    </AlertDescription>
                                </Alert>
                            ) : cannotOrderTag ? (
                                <Alert status="error" borderRadius="md" border="1px solid" borderColor="red.300" bg="red.50">
                                    <AlertIcon color="red.500" />
                                    <AlertDescription fontSize="sm" color="red.800">
                                        You cannot order a <strong>{cannotOrderTag}</strong>. Please remove those tags in the header and retry.
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <Box bg="orange.50" p={3} borderRadius="md" border="1px solid" borderColor="orange.200">
                                    <Text fontSize="sm" color="orange.800">
                                        <strong>Note:</strong> Once placed, the order will be processed and cannot be modified.
                                    </Text>
                                </Box>
                            )}

                            {data.scheduledDate && (
                                <Alert status="warning" borderRadius="md" border="1px solid" borderColor="yellow.300" bg="yellow.50">
                                    <AlertIcon color="yellow.600" />
                                    <AlertDescription fontSize="sm" color="yellow.900">
                                        This order is already scheduled to be placed on <strong>{formatScheduledDateLong(data.scheduledDate)}</strong>. Placing it now will override that schedule.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <Box>
                                <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={2}>
                                    Order Details
                                </Text>
                                <Box
                                    bg="white"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    overflow="hidden"
                                >
                                    <DetailRow
                                        label="RO"
                                        tooltip="This will appear as the purchase order number on the invoice received from vendors on this order."
                                    >
                                        {isEditingRoNumber ? (
                                            <Input
                                                value={editedRoNumber}
                                                onChange={(e) => setEditedRoNumber(e.target.value.trim())}
                                                onBlur={handleRoNumberSave}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleRoNumberSave();
                                                    } else if (e.key === 'Escape') {
                                                        setIsEditingRoNumber(false);
                                                        setEditedRoNumber(data?.roNumber?.trim() || '');
                                                    }
                                                }}
                                                placeholder="Enter RO number"
                                                size="sm"
                                                width="220px"
                                                autoFocus
                                                isDisabled={updateOrderDetailsMutation.isPending}
                                                fontSize="sm"
                                                color="gray.900"
                                                textAlign="right"
                                            />
                                        ) : (
                                            <HStack spacing={2} justify="flex-end">
                                                <Text fontSize="sm" color="gray.900" fontWeight="medium">
                                                    {editedRoNumber || '—'}
                                                </Text>
                                                <IconButton
                                                    aria-label="Edit RO Number"
                                                    icon={<Icon as={TbPencil as React.ElementType} w={3} h={3} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => setIsEditingRoNumber(true)}
                                                    isDisabled={updateOrderDetailsMutation.isPending}
                                                    _hover={{ bg: "gray.100" }}
                                                    minW="auto"
                                                    h="auto"
                                                    p={1}
                                                />
                                            </HStack>
                                        )}
                                    </DetailRow>

                                    {editedRoNumber.trim().length > 0 && !/\d/.test(editedRoNumber) && (
                                        <Box px={4} pb={3} pt={0} bg="white">
                                            <Alert status="warning" borderRadius="md" size="sm">
                                                <AlertIcon />
                                                <Text fontSize="xs">
                                                    <strong>Warning:</strong> RO number contains no numeric digits.
                                                </Text>
                                            </Alert>
                                        </Box>
                                    )}

                                    <DetailRow
                                        label="Order Type"
                                        tooltip="We allow for delayed ordering, so you can select the date you want it ordered and we will place the order at 9am local on that date."
                                    >
                                        <HStack spacing={2} justify="flex-end" flex={1}>
                                            <Menu placement="bottom-end" autoSelect={false}>
                                                <MenuButton
                                                    as={Button}
                                                    size="sm"
                                                    variant="outline"
                                                    bg="white"
                                                    borderColor="gray.300"
                                                    rightIcon={<Icon as={TbChevronDown as React.ElementType} boxSize={3.5} />}
                                                    fontWeight="medium"
                                                    fontSize="sm"
                                                    color="gray.800"
                                                    minW="200px"
                                                    textAlign="right"
                                                    _hover={{ bg: 'gray.50' }}
                                                    _active={{ bg: 'gray.100' }}
                                                    _focus={{ boxShadow: 'none' }}
                                                >
                                                    {orderTypeDisplay}
                                                </MenuButton>
                                                <MenuList minW="220px" py={1}>
                                                    {(Object.keys(ORDER_TYPE_LABELS) as OrderType[]).map((opt) => (
                                                        <MenuItem
                                                            key={opt}
                                                            onClick={() => setOrderType(opt)}
                                                            fontSize="sm"
                                                            color="gray.800"
                                                            bg={orderType === opt ? '#E6F1FD' : undefined}
                                                            _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                                            fontWeight={orderType === opt ? 'semibold' : 'normal'}
                                                        >
                                                            {ORDER_TYPE_LABELS[opt]}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </Menu>
                                            {orderType === 'custom' && (
                                                <Box w="160px">
                                                    <DatePicker
                                                        value={customDate}
                                                        onChange={setCustomDate}
                                                        size="sm"
                                                        fontSize="sm"
                                                    />
                                                </Box>
                                            )}
                                        </HStack>
                                    </DetailRow>

                                    <DetailRow label="Total Parts Selected">
                                        <Badge colorScheme="blue" variant="solid" px={2} py={1} fontSize="xs" borderRadius="md">
                                            {data.totalParts}
                                        </Badge>
                                    </DetailRow>

                                    <DetailRow label="Total Price">
                                        <Text fontSize="sm" color="gray.900" fontWeight="semibold">
                                            {formatPrice(data.buyPrice)}
                                        </Text>
                                    </DetailRow>

                                    <DetailRow label="Total Margin" isLast>
                                        <HStack spacing={2} justify="flex-end">
                                            <Text fontSize="sm" color="gray.900" fontWeight="semibold">
                                                {formatPrice(data.marginTotal)}
                                            </Text>
                                            <Badge
                                                colorScheme={(data.marginPct) >= 0.3 ? 'green' : data.marginPct >= 0.2 ? 'orange' : 'red'}
                                                variant="solid"
                                                px={2}
                                                py={1}
                                                borderRadius="md"
                                                fontSize="xs"
                                                fontWeight="bold"
                                            >
                                                {((data?.marginPct ?? 0) * 100).toFixed(1)}%
                                            </Badge>
                                        </HStack>
                                    </DetailRow>
                                </Box>
                            </Box>

                            {data.suppliers && data.suppliers.length > 0 && (
                                <Box>
                                    <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={2}>
                                        Supplier Breakdown ({data.suppliers.length} {data.suppliers.length === 1 ? 'supplier' : 'suppliers'})
                                    </Text>
                                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" overflow="hidden" maxH="240px" overflowY="auto">
                                        <Table size="sm" variant="simple">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th fontSize="xs" color="gray.600">Supplier</Th>
                                                    <Th fontSize="xs" color="gray.600" isNumeric>Parts</Th>
                                                    <Th fontSize="xs" color="gray.600" isNumeric>List Price</Th>
                                                    <Th fontSize="xs" color="gray.600" isNumeric>Margin %</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {data.suppliers.map((supplier) => (
                                                    <Tr key={supplier.supplierId}>
                                                        <Td fontSize="sm" color="gray.800" fontWeight="medium">
                                                            {supplier.supplierName}
                                                        </Td>
                                                        <Td fontSize="sm" color="gray.700" isNumeric>
                                                            {supplier.totalParts}
                                                        </Td>
                                                        <Td fontSize="sm" color="gray.700" isNumeric>
                                                            {formatPrice(supplier.listPrice)}
                                                        </Td>
                                                        <Td fontSize="sm" color="gray.700" isNumeric>
                                                            {supplier.listPrice > 0 ? `${(((supplier.listPrice - supplier.buyPrice) / supplier.listPrice) * 100).toFixed(1)}%` : '0.0%'}
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                </Box>
                            )}

                            {data.hasFlaggedParts && (
                                <Alert status="info" borderRadius="md" variant="left-accent">
                                    <AlertIcon />
                                    <AlertDescription fontSize="sm">
                                        This order has flagged parts. All flagged parts will be left for the central parts team to review.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </VStack>
                    )}
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3}>
                        <Button
                            size="action"
                            variant="outlineGray"
                            onClick={onClose}
                            isDisabled={placeOrder.isPending || isLoading}
                        >
                            Cancel
                        </Button>
                        <PartBayButton
                            size="action"
                            colorScheme='partbay-blue'
                            onClick={handleConfirmPlaceOrder}
                            isLoading={placeOrder.isPending}
                            isDisabled={isLoading || !data || data.totalParts === 0 || isQuoteWorkflow || hasInvalidMessage || !hasRoNumber}
                            loadingText="Placing Order..."
                            variant='solid'
                            text='Place Order'
                        />
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <Modal isOpen={isOeConfirmOpen} onClose={() => setIsOeConfirmOpen(false)} size="md" isCentered>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader color="gray.800" fontSize="lg" fontWeight="semibold">
                    Confirm Non-OE Parts
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody py={5}>
                    <Alert status="warning" borderRadius="md" border="1px solid" borderColor="orange.200" bg="orange.50">
                        <AlertIcon color="orange.500" />
                        <AlertDescription fontSize="sm" color="orange.900">
                            You're attempting to order non-OE parts for a certified location. Please confirm the order placement again below.
                        </AlertDescription>
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={3}>
                        <Button
                            size="action"
                            variant="outlineGray"
                            onClick={() => setIsOeConfirmOpen(false)}
                            isDisabled={placeOrder.isPending}
                        >
                            Cancel
                        </Button>
                        <PartBayButton
                            size="action"
                            colorScheme='partbay-blue'
                            onClick={doPlaceOrder}
                            isLoading={placeOrder.isPending}
                            loadingText="Placing Order..."
                            variant='solid'
                            text='Confirm Order Placement'
                        />
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
        </>
    );
};

interface DetailRowProps {
    label: string;
    tooltip?: string;
    isLast?: boolean;
    children: React.ReactNode;
}

const DetailRow = ({ label, tooltip, isLast, children }: DetailRowProps) => (
    <Flex
        justify="space-between"
        align="center"
        px={4}
        py={3}
        borderBottom={isLast ? undefined : '1px solid'}
        borderColor="gray.100"
        bg="white"
        minH="52px"
    >
        <HStack spacing={1.5} flexShrink={0}>
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
                {label}
            </Text>
            {tooltip && (
                <Tooltip
                    label={tooltip}
                    placement="top"
                    hasArrow
                    bg="gray.700"
                    color="white"
                    fontSize="xs"
                    borderRadius="md"
                    px={3}
                    py={2}
                    maxW="320px"
                >
                    <Box as="span" display="inline-flex" cursor="help" color="gray.400" _hover={{ color: 'gray.600' }}>
                        <Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />
                    </Box>
                </Tooltip>
            )}
        </HStack>
        <Box>{children}</Box>
    </Flex>
);

export default PlaceOrderConfirmationModal;
